import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import Icon from '../../Icons'
import { INTEGRATION_STATUSES, getCategoryLabels } from '../constants'

const IntegrationModal = ({ integration, onClose, onRequest, isLoading, isRequestSent }) => {
  const { t } = useTranslation(['integrations', 'common'])
  const categoryLabels = getCategoryLabels(t)
  const handleRequestInfo = async () => {
    await onRequest({
      type: 'integration-info',
      integrationName: integration.name,
    })
  }

  const getStatusConfig = (status) => {
    switch (status) {
      case INTEGRATION_STATUSES.AVAILABLE:
        return {
          text: t('integrations:statuses.available'),
          icon: 'checkmark',
          className: 'status-available',
        }
      case INTEGRATION_STATUSES.PENDING:
        return {
          text: t('integrations:statuses.pending'),
          icon: 'clock',
          className: 'status-pending',
        }
      default:
        return {
          text: status,
          icon: 'cross',
          className: 'status-no-api',
        }
    }
  }

  const statusConfig = getStatusConfig(integration.status)

  if (isRequestSent) {
    return (
      <div className="integration-modal-overlay" onClick={onClose}>
        <div
          className="integration-modal request-integration-modal"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="integration-modal__header">
            <h2>{t('integrations:modal.title')}</h2>
            <button className="integration-modal__close" onClick={onClose}>
              <Icon icon="cross" width="24" height="24" />
            </button>
          </div>
          <div className="integration-modal__body">
            <div className="request-sent-content">
              <div className="request-sent-icon">
                <Icon icon="checkmark" width="40" height="40" viewBox="0 0 30 30" />
              </div>
              <h3>{t('integrations:shared.requestSentTitle')}</h3>
              <p>{t('integrations:modal.requestSentBody')}</p>
              <button className="button button-primary" onClick={onClose}>
                {t('common:actions.close')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="integration-modal-overlay" onClick={onClose}>
      <div className="integration-modal" onClick={(e) => e.stopPropagation()}>
        <div className="integration-modal__header">
          <h2>{t('integrations:modal.title')}</h2>
          <button className="integration-modal__close" onClick={onClose}>
            <Icon icon="cross" width="24" height="24" />
          </button>
        </div>
        <div className="integration-modal__body">
          <div className="integration-modal__intro">
            <div className="integration-modal__logo">
              <img
                src={integration.logo}
                alt={t('integrations:shared.logoAlt', { name: integration.name })}
                className="integration-modal__logo-image"
              />
            </div>
            <div className="integration-modal__info">
              <div className="integration-modal__status-row">
                <div className="integration-modal__title-section">
                  <div className={`integration-modal__status ${statusConfig.className}`}>
                    <span className="status-text">{statusConfig.text}</span>
                    <Icon
                      icon={statusConfig.icon}
                      className="status-icon"
                      width="9"
                      height="9"
                      viewBox={
                        statusConfig.icon === 'clock'
                          ? '0 0 35 35'
                          : statusConfig.icon === 'checkmark'
                            ? '0 0 30 30'
                            : '0 0 20 20'
                      }
                    />
                  </div>
                  <h3 className="integration-modal__name">{integration.name}</h3>
                </div>
                <button
                  className="button button-primary integration-modal__request-btn"
                  onClick={handleRequestInfo}
                  disabled={isLoading}
                >
                  {isLoading
                    ? t('integrations:modal.requestInfoSendingButton')
                    : t('integrations:modal.requestInfoButton')}
                </button>
              </div>
              <p className="integration-modal__description">{integration.description}</p>
              <div className="integration-modal__meta">
                <div className="integration-modal__category">
                  <span>{categoryLabels[integration.category] || integration.category}</span>
                </div>
                {integration.website && (
                  <a
                    href={`https://${integration.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="integration-modal__website"
                  >
                    <Icon icon="globe" width="20" height="20" />
                    <span>{integration.website}</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          {integration.features && integration.features.length > 0 && (
            <div className="integration-modal__section">
              <h4>{t('integrations:modal.featuresHeader')}</h4>
              <div className="integration-modal__features">
                {integration.features.map((feature, index) => (
                  <div key={index} className="integration-modal__feature">
                    <Icon
                      icon="checkmark"
                      className="feature-check"
                      width="20"
                      height="20"
                      viewBox="0 0 30 30"
                    />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {integration.howItWorks && (
            <div className="integration-modal__section">
              <h4>{t('integrations:modal.howItWorksHeader')}</h4>
              <p className="integration-modal__how-it-works">{integration.howItWorks}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

IntegrationModal.propTypes = {
  integration: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    category: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
    website: PropTypes.string,
    features: PropTypes.arrayOf(PropTypes.string),
    howItWorks: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onRequest: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isRequestSent: PropTypes.bool,
}

export default IntegrationModal
